'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { BcmLogo } from '@/components/BcmLogo';
import Link from 'next/link';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import { toggleMobileMenu } from '@/utils/toggleMobileMenu';
import { init_classic_menu_resize } from '@/utils/menuToggle';
import { scrollToElement } from '@/utils/scrollToElement';
import LanguageSelect from './components/LanguageSelect';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { createPortal } from 'react-dom';
import { conOrigen } from '@/lib/origen-suscripcion';
import { VISIBLES } from '@/lib/idiomas';

/* ===========================================================
  FEATURE SWITCH
  =========================================================== */
const ENABLE_TOOLS_MENU = true;

/* ===========================================================
  PRODUCT ACCESS (IDs hardcodeados por app)
  =========================================================== */
export const PRODUCT_ACCESS = {
  
 
  //MODOS ILUSTADOS = ARMONIZADOR IDS: 11,12,13,14
  ARMONIZADOR: new Set([11, 12, 13, 14, 15, 16, 17, 18, 19]),

  //ARMONIA ILUSTRADA 1 Y 2 = BUSCADOR DE ACORDES, ACORDES VECINOS IDS: 21,22,23,24
  ACORDES_VECINOS: new Set([1, 2, 3, 4, 5, 6, 7, 8, 9, 21, 22, 23, 24, 25, 26, 27, 28, 29]),
  BUSCADOR_ACORDES: new Set([1, 2, 3, 4, 5, 6, 7, 8, 9, 21, 22, 23, 24, 25, 26, 27, 28, 29, 41, 42, 43, 44, 45, 46, 47, 48, 51, 52, 53, 54, 55, 56, 57, 58,]),

  

  // LIBRO TENSIONES
  VISUALIZADOR_INTERVALOS: new Set([31,32,33,34,35,36,37,38,39, 51, 52, 53, 54, 55, 56, 57, 58]),
  POLYACORDES: new Set([31,32,33,34,35,36,37,38,39]),
  VISUALIZADOR_FRECUENCIAS: new Set([31,32,33,34,35,36,37,38,39]),
  VISUALIZADOR_ARMONICOS: new Set([31,32,33,34,35,36,37,38,39]),

  // 3D BOOK
  BOOK3D: new Set([1, 2, 3, 4, 5, 6, 7, 8, 9]),

  // PIANO ROLL
  PIANO_ROLL: new Set([51, 52, 53, 54, 55, 56, 57, 58]),

  // GLUE MAGICO PARA GUITARRISTAS, IDS: 41,42,43,44,45,46,47
  CHORD_INVERSIONS: new Set([41, 42, 43, 44, 45, 46, 47, 48]),
  // El editor de mástil es la evolución de chord-inversions: mismo libro,
  // mismos productos.
  NECK_EDITOR: new Set([41, 42, 43, 44, 45, 46, 47, 48]),
  TRIADAS_123: new Set([41, 42, 43, 44, 45, 46, 47, 48]),
  ACORDES_PIANO_ROLL: new Set([41, 42, 43, 44, 45, 46, 47, 48, 51, 52, 53, 54, 55, 56, 57, 58]),

  // CLARINETE
  CLARINETE: new Set([1001]),

  // BCM NODES — acceso para cualquier comprador de libro (todos los IDs menos 1001)
  BCM_NODES: new Set([
    1, 2, 3, 4, 5, 6, 7, 8, 9,
    11, 12, 13, 14, 15, 16, 17, 18, 19,
    21, 22, 23, 24, 25, 26, 27, 28, 29,
    31, 32, 33, 34, 35, 36, 37, 38, 39,
    41, 42, 43, 44, 45, 46, 47, 48,
    51, 52, 53, 54, 55, 56, 57, 58,
  ]),

  // VUELTAS — solo suscriptores (accessMode: 'subscription' en APP_UI): el set
  // queda vacío a propósito, ningún libro habilita la app.
  VUELTAS: new Set([]),

  // APRENDER_PARTITURA — solo suscriptores, igual que Vueltas. Set vacío a
  // propósito: ningún libro la habilita. Ojo que acá el gate del front NO es
  // decorativo, porque la app guarda el historial de práctica contra
  // admin-back, y allá la protege EnsureNoteReadingAccess.
  APRENDER_PARTITURA: new Set([]),

  // METRONOMO_DISTORSIONADO — solo suscriptores, igual que Vueltas. Set vacío a
  // propósito: ningún libro la habilita. El gate del front NO es decorativo:
  // la app guarda borrador, presets y sesiones en admin-back, y allá la protege
  // EnsureMetronomeAccess.
  METRONOMO_DISTORSIONADO: new Set([]),

  // JUEGO_DE_ACORDES — solo suscriptores, igual que Vueltas. Set vacío a
  // propósito: ningún libro la habilita. El gate del front NO es decorativo:
  // la app guarda cada intento y su config en admin-back, y allá la protege
  // EnsureChordGameAccess.
  JUEGO_DE_ACORDES: new Set([]),

  // CHORD_TILING (Chords Wave function collapse) — solo suscriptores, como
  // Vueltas: set vacío a propósito. No guarda nada.
  CHORD_TILING: new Set([]),

  // APRENDE_EL_CIRCULO_DE_QUINTAS — solo suscriptores, como Vueltas: set vacío a
  // propósito. No guarda nada en el servidor (los tiempos quedan en el navegador).
  APRENDE_EL_CIRCULO_DE_QUINTAS: new Set([]),

  // COLECCIONA_LAS_12_NOTAS — solo suscriptores, como Vueltas: set vacío a
  // propósito (es del libro Música Ilustrada de Cero, que todavía está en
  // preventa). No guarda nada en el servidor (la partida queda en el navegador).
  COLECCIONA_LAS_12_NOTAS: new Set([]),

  // CAMINOS_NEO_RIEMANNIANOS — solo suscriptores, como Vueltas: set vacío a
  // propósito. No guarda nada en ningún lado (ni en el navegador).
  CAMINOS_NEO_RIEMANNIANOS: new Set([]),

  // MOSCA — solo suscriptores, como Vueltas: set vacío a propósito.
  MOSCA: new Set([]),

  // GRAFOS 3D POR MATRIZ — gratis (accessMode: 'free' en APP_UI): la abre
  // cualquiera, con o sin cuenta. Set vacío a propósito; canAccess ni lo mira.
  GRAFOS_3D_POR_MATRIZ: new Set([]),

  // MANDALAS — gratis (accessMode: 'free' en APP_UI): la abre cualquiera, con
  // o sin cuenta. Set vacío a propósito; canAccess ni lo mira.
  MANDALAS: new Set([]),

  // ESCALAS ILUSTRADAS (libro 61) — material adjunto: los 11 gráficos
  // interactivos del libro. Gatea por producto 61, igual que el libro mismo.
  ESCALAS_ILUSTRADAS: new Set([61, 62, 63, 64, 65, 66, 67, 68]),
};

/* ===========================================================
  ACCESO POR SUSCRIPCION

  El cartel de las apps de suscripcion NO habla de la app: habla de la
  suscripcion, asi que el texto es el mismo para todas (pedido de Fede,
  20-sep-2026: "no deberia cambiar por app"). Vive UNA sola vez aca y se
  esparce con ...SUSCRIPCION en cada app. Cambiarlo aca lo cambia en las
  cuatro pantallas y tambien en el modal de la grilla, que lee lo mismo.

  Para sumar una app de suscripcion: ...SUSCRIPCION y nada mas. Lo que NO
  va aca es una app de libro: la suscripcion no las destraba (canAccess
  mira el set de productos), asi que ofrecerles el cafe seria cobrar por
  un acceso que ese pago no da.
  =========================================================== */
const SUSCRIPCION = {
  i18n: {
    message: 'apps:gate.subscription_message',
    cta:     'apps:gate.subscription_cta',
  },
  accessMode: 'subscription',
  buyPath: '/subscribe',
};

/* ===========================================================
  APP CONFIG (i18n keys todo en 'common')
  =========================================================== */
export const APP_UI = {
  VECINOS: {
    nameKey: 'apps:tools.acordes_vecinos.name',
    href: '/apps/acordes-vecinos',
    icons: {
      active: '/assets/images/apps/AcordesVecinosTST_Activo.svg',
      block:  '/assets/images/apps/AcordesVecinosTST_Block.svg',
    },
    i18n: {
      message: 'apps:acordes_vecinos.lock_message',
      cta:     'apps:acordes_vecinos.buy_cta',
    },
    buyPath: '/checkout-pay?products=21',
    accessSetKey: 'ACORDES_VECINOS',
  },
  BUSCADOR_ACORDES: {
    nameKey: 'apps:tools.buscador_acordes.name',
    href: '/apps/buscador-acordes',
    icons: {
      active: '/assets/images/apps/superestructuras-de-acordes.png',
      block:  '/assets/images/apps/superestructuras-de-acordes.png',
    },
    i18n: {
      message: 'apps:buscador_acordes.lock_message',
      cta:     'apps:buscador_acordes.buy_cta',
    },
    buyPath: '/checkout-pay?products=21',
    accessSetKey: 'BUSCADOR_ACORDES',
  },
  ARMONIZADOR: {
    nameKey: 'apps:tools.armonizador.name',
    href: '/app/armonizador/index.html',
    icons: {
      active: '/assets/images/apps/ArmonizadorYComparador_Activo.svg',
      block:  '/assets/images/apps/ArmonizadorYComparador_Block.svg',
    },
    i18n: {
      message: 'apps:armonizador.lock_message',
      cta:     'apps:armonizador.buy_cta',
    },
    buyPath: '/checkout-pay?products=11',
    accessSetKey: 'ARMONIZADOR',
  },
  VISUALIZADOR_INTERVALOS: {
    nameKey: 'apps:tools.visualizador_intervalos.name',
    href: '/apps/visualizador-intervalos',
    icons: {
      active: '/assets/images/apps/06.png',
      block:  '/assets/images/apps/06.png',
    },
    i18n: {
      message: 'apps:visualizador_intervalos.lock_message',
      cta:     'apps:visualizador_intervalos.buy_cta',
    },
    buyPath: '/checkout-pay?products=31',
    accessSetKey: 'VISUALIZADOR_INTERVALOS',
  },
  POLYACORDES: {
    nameKey: 'apps:tools.polyacordes.name',
    href: '/apps/polyacordes',
    icons: {
      active: '/assets/images/apps/03.png',
      block:  '/assets/images/apps/03.png',
    },
    i18n: {
      message: 'apps:polyacordes.lock_message',
      cta:     'apps:polyacordes.buy_cta',
    },
    buyPath: '/checkout-pay?products=31',
    accessSetKey: 'POLYACORDES',
  },
  VISUALIZADOR_FRECUENCIAS: {
    nameKey: 'apps:tools.visualizador_de_frecuencias.name',
    href: '/apps/visualizador-frecuencias',
    icons: {
      active: '/assets/images/apps/04.png',
      block:  '/assets/images/apps/04.png',
    },
    i18n: {
      message: 'apps:visualizador_de_frecuencias.lock_message',
      cta:     'apps:visualizador_de_frecuencias.buy_cta',
    },
    buyPath: '/checkout-pay?products=31',
    accessSetKey: 'VISUALIZADOR_FRECUENCIAS',
  },
  VISUALIZADOR_ARMONICOS: {
    nameKey: 'apps:tools.visualizador_de_armonicos.name',
    href: '/apps/visualizador-armonicos',
    icons: {
      active: '/assets/images/apps/11.png',
      block:  '/assets/images/apps/11.png',
    },
    i18n: {
      message: 'apps:visualizador_de_armonicos.lock_message',
      cta:     'apps:visualizador_de_armonicos.buy_cta',
    },
    buyPath: '/checkout-pay?products=31',
    accessSetKey: 'VISUALIZADOR_ARMONICOS',
  },
  BOOK3D: {
    nameKey: 'apps:tools.book3d.name',
    href: '/apps/3d-book',
    icons: {
      active: '/assets/images/apps/3dbook.png',
      block:  '/assets/images/apps/3dbook.png',
    },
    i18n: {
      message: 'apps:book3d.lock_message',
      cta:     'apps:book3d.buy_cta',
    },
    buyPath: '/checkout-pay?products=1',
    accessSetKey: 'BOOK3D',
  },
  PIANO_ROLL: {
    nameKey: 'apps:tools.piano_roll.name',
    href: '/apps/piano-roll',
    icons: {
      active: '/assets/images/apps/acordes-piano-roll.png',
      block: '/assets/images/apps/acordes-piano-roll.png',
    },
    i18n: {
      message: 'apps:piano_roll.lock_message',
      cta:     'apps:piano_roll.buy_cta',
    },
    buyPath: '/checkout-pay?products=51',
    accessSetKey: 'PIANO_ROLL',
  },
  CHORD_INVERSIONS: {
    nameKey: 'apps:tools.chord_inversions.name',
    href: '/apps/chord-inversions',
    icons: {
      active: '/assets/images/apps/toda-la-guitarra.png',
      block:  '/assets/images/apps/toda-la-guitarra.png',
    },
    i18n: {
      message: 'apps:chord_inversions.lock_message',
      cta:     'apps:chord_inversions.buy_cta',
    },
    buyPath: '/checkout-pay?products=41',
    accessSetKey: 'GLUE_MAGICO_PARA_GUITARRISTAS',
  },

  NECK_EDITOR: {
    nameKey: 'apps:neck_editor.name',
    href: '/apps/neck-editor',
    icons: {
      active: '/assets/images/apps/editor-de-mastil.png',
      block:  '/assets/images/apps/editor-de-mastil.png',
    },
    i18n: {
      message: 'apps:neck_editor.lock_message',
      cta:     'apps:neck_editor.buy_cta',
    },
    buyPath: '/checkout-pay?products=41',
    accessSetKey: 'GLUE_MAGICO_PARA_GUITARRISTAS',
  },

  TRIADAS_123: {
    nameKey: 'apps:tools.triadas_123.name',
    href: '/apps/triadas-123',
    icons: {
      active: '/assets/images/apps/triadas-123.png',
      block:  '/assets/images/apps/triadas-123.png',
    },
    i18n: {
      message: 'apps:triadas_123.lock_message',
      cta:     'apps:triadas_123.buy_cta',
    },
    buyPath: '/checkout-pay?products=41',
    accessSetKey: 'GLUE_MAGICO_PARA_GUITARRISTAS',
  },

  ACORDES_PIANO_ROLL: {
    nameKey: 'apps:tools.acordes_piano_roll.name',
    href: '/apps/acordes-piano-roll',
    icons: {
      active: '/assets/images/apps/piano-roll.png',
      block: '/assets/images/apps/piano-roll.png',
    },
    i18n: {
      message: 'apps:acordes_piano_roll.lock_message',
      cta:     'apps:acordes_piano_roll.buy_cta',
    },
    buyPath: '/checkout-pay?products=41',
    accessSetKey: 'GLUE_MAGICO_PARA_GUITARRISTAS',
  },
  CLARINETE: {
    nameKey: 'apps:tools.clarinete.name',
    href: '/apps/clarinete',
    icons: {
      active: '/assets/images/apps/clarinete.png',
      block:  '/assets/images/apps/clarinete.png',
    },
    i18n: {
      message: 'apps:clarinete.lock_message',
      cta:     'apps:clarinete.buy_cta',
    },
    buyPath: '/checkout-pay?products=1001',
    accessSetKey: 'CLARINETE',
  },
  BCM_NODES: {
    nameKey: 'apps:bcm_nodes.name',
    href: '/apps/bcm-nodes',
    icons: {
      active: '/assets/images/apps/bcm-nodes.png',
      block:  '/assets/images/apps/bcm-nodes.png',
    },
    // Exclusiva para suscriptores (no para compradores de libros): el acceso se
    // resuelve por suscripción activa y la CTA lleva a /subscribe.
    ...SUSCRIPCION,
    accessSetKey: 'BCM_NODES',
  },
  VUELTAS: {
    nameKey: 'apps:vueltas.name',
    href: '/apps/vueltas',
    icons: {
      active: '/assets/images/apps/v43/vueltas.webp',
      block:  '/assets/images/apps/v43/vueltas.webp',
    },
    // Igual que BCM Nodes: exclusiva para suscriptores, la CTA lleva a /subscribe.
    ...SUSCRIPCION,
    accessSetKey: 'VUELTAS',
  },
  APRENDER_PARTITURA: {
    nameKey: 'apps:aprender_partitura.name',
    href: '/apps/aprender-partitura',
    icons: {
      active: '/assets/images/apps/v43/aprender-partitura.webp',
      block:  '/assets/images/apps/v43/aprender-partitura.webp',
    },
    // Exclusiva para suscriptores. A diferencia del resto de las apps con gate,
    // esta ESCRIBE: guarda el historial de práctica en admin-back, y el gate de
    // allá mira hasAccess(), que incluye los 14 días de gracia cuando rebota la
    // tarjeta. Por eso su page.jsx lee `has_access` y no `active`.
    ...SUSCRIPCION,
    accessSetKey: 'APRENDER_PARTITURA',
  },
  METRONOMO_DISTORSIONADO: {
    nameKey: 'apps:metronomo_distorsionado.name',
    href: '/apps/metronomo-distorsionado',
    icons: {
      active: '/assets/images/apps/v43/metronomo-distorsionado.webp',
      block:  '/assets/images/apps/v43/metronomo-distorsionado.webp',
    },
    // Exclusiva para suscriptores. Como Aprender Partitura, ESCRIBE: guarda
    // borrador, presets y sesiones en admin-back, y el gate de allá
    // (EnsureMetronomeAccess) mira hasAccess(), que incluye los 14 días de
    // gracia. Por eso su page.jsx lee `has_access` y no `active`.
    ...SUSCRIPCION,
    accessSetKey: 'METRONOMO_DISTORSIONADO',
  },
  JUEGO_DE_ACORDES: {
    nameKey: 'apps:tools.juego_de_acordes.name',
    href: '/apps/juego-de-acordes',
    icons: {
      active: '/assets/images/apps/v43/juego-de-acordes.webp',
      block:  '/assets/images/apps/v43/juego-de-acordes.webp',
    },
    // Exclusiva para suscriptores. Como Aprender Partitura, ESCRIBE: guarda cada
    // intento, el agregado por acorde y la config en admin-back, y el gate de allá
    // (EnsureChordGameAccess) mira hasAccess(), que incluye los 14 días de
    // gracia. Por eso su page.jsx lee `has_access` y no `active`.
    ...SUSCRIPCION,
    accessSetKey: 'JUEGO_DE_ACORDES',
  },
  CHORD_TILING: {
    nameKey: 'apps:tools.chord_tiling.name',
    href: '/apps/chord-tiling',
    icons: {
      active: '/assets/images/apps/v43/chord-tiling.webp',
      block:  '/assets/images/apps/v43/chord-tiling.webp',
    },
    // Exclusiva para suscriptores, la CTA lleva a /subscribe. No escribe nada.
    ...SUSCRIPCION,
    accessSetKey: 'CHORD_TILING',
  },
  APRENDE_EL_CIRCULO_DE_QUINTAS: {
    nameKey: 'apps:tools.aprende_el_circulo_de_quintas.name',
    href: '/apps/aprende-el-circulo-de-quintas',
    icons: {
      active: '/assets/images/apps/v43/aprende-el-circulo-de-quintas.webp',
      block:  '/assets/images/apps/v43/aprende-el-circulo-de-quintas.webp',
    },
    // Exclusiva para suscriptores, la CTA lleva a /subscribe. No escribe en ningún
    // servidor: los tiempos de cada uno quedan en su navegador.
    ...SUSCRIPCION,
    accessSetKey: 'APRENDE_EL_CIRCULO_DE_QUINTAS',
  },
  COLECCIONA_LAS_12_NOTAS: {
    nameKey: 'apps:tools.colecciona_las_12_notas.name',
    href: '/apps/colecciona-las-12-notas',
    icons: {
      active: '/assets/images/apps/v43/colecciona-las-12-notas-v2.webp',
      block:  '/assets/images/apps/v43/colecciona-las-12-notas-v2.webp',
    },
    // Exclusiva para suscriptores, la CTA lleva a /subscribe. No escribe en ningún
    // servidor: la partida y los récords quedan en el navegador.
    ...SUSCRIPCION,
    accessSetKey: 'COLECCIONA_LAS_12_NOTAS',
  },
  CAMINOS_NEO_RIEMANNIANOS: {
    nameKey: 'apps:tools.caminos_neo_riemannianos.name',
    href: '/apps/caminos-neo-riemannianos',
    icons: {
      active: '/assets/images/apps/v43/caminos-neo-riemannianos.webp',
      block:  '/assets/images/apps/v43/caminos-neo-riemannianos.webp',
    },
    // Exclusiva para suscriptores, la CTA lleva a /subscribe. No escribe en ningún
    // servidor: los caminos se calculan en el navegador.
    ...SUSCRIPCION,
    accessSetKey: 'CAMINOS_NEO_RIEMANNIANOS',
  },
  MOSCA: {
    nameKey: 'apps:mosca.name',
    href: '/apps/mosca',
    icons: {
      active: '/assets/images/apps/v43/mosca.webp',
      block:  '/assets/images/apps/v43/mosca.webp',
    },
    // Exclusiva para suscriptores, la CTA lleva a /subscribe.
    ...SUSCRIPCION,
    accessSetKey: 'MOSCA',
  },
  GRAFOS_3D_POR_MATRIZ: {
    nameKey: 'apps:tools.grafos_3d_por_matriz.name',
    href: '/apps/grafos-3d-por-matriz',
    icons: {
      active: '/assets/images/apps/v43/grafos-3d-por-matriz-v2.webp',
      block:  '/assets/images/apps/v43/grafos-3d-por-matriz-v2.webp',
    },
    i18n: {
      message: 'apps:grafos_3d_por_matriz.lock_message',
      cta:     'apps:grafos_3d_por_matriz.buy_cta',
    },
    // Gratis: la abre cualquiera, aun sin cuenta, así que el paywall nunca sale.
    // i18n y buyPath quedan para que la forma sea la de las demás.
    accessMode: 'free',
    buyPath: '/subscribe',
    accessSetKey: 'GRAFOS_3D_POR_MATRIZ',
  },
  MANDALAS: {
    nameKey: 'apps:mandalas.name',
    href: '/apps/mandalas',
    icons: {
      active: '/assets/images/apps/v43/mandalas.webp',
      block:  '/assets/images/apps/v43/mandalas.webp',
    },
    i18n: {
      message: 'apps:mandalas.lock_message',
      cta:     'apps:mandalas.buy_cta',
    },
    // Gratis: la abre cualquiera, aun sin cuenta, así que el paywall nunca sale.
    accessMode: 'free',
    buyPath: '/login',
    accessSetKey: 'MANDALAS',
  },
  MATERIAL_ADJUNTO_EI: {
    nameKey: 'apps:tools.material_adjunto_escalas_ilustradas.name',
    href: '/apps/material-adjunto-escalas-ilustradas',
    icons: {
      active: '/assets/images/apps/v43/material-adjunto-escalas-ilustradas.webp',
      block:  '/assets/images/apps/v43/material-adjunto-escalas-ilustradas.webp',
    },
    i18n: {
      message: 'apps:material_adjunto_escalas_ilustradas.lock_message',
      cta:     'apps:material_adjunto_escalas_ilustradas.buy_cta',
    },
    buyPath: '/checkout-pay?products=61',
    accessSetKey: 'ESCALAS_ILUSTRADAS',
  },
};
APP_UI.VECINOS.accessSetKey = 'ACORDES_VECINOS';

/* ========= Modal montado en <body> via Portal ========= */
function Modal({ open, onClose, children }) {
  useEffect(() => {
    if (!open) return;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open || typeof window === 'undefined') return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 100000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 520,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 20px 40px rgba(0,0,0,0.18)',
          padding: 24,
        }}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

export default function HeaderPreview({ hideMenu = false, disableLogoLink = false }) {
  const { t, i18n } = useTranslation();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  const [user, setUser] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [userProducts, setUserProducts] = useState([]);
  const [isSubscriber, setIsSubscriber] = useState(false);

  // 🔑 Referencias para Firebase (cargado lazy)
  const firebaseAuthRef = useRef(null);
  const authInstanceRef = useRef(null);

  // Modales
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAppKey, setModalAppKey] = useState(null);

  // 🔑 LAZY LOAD de Firebase Auth - solo después del render inicial
  useEffect(() => {
    let unsubscribe = () => {};
    
    const initAuth = async () => {
      try {
        // 🔒 Gate: si NO hay sesión Firebase persistida (visitante anónimo, el
        // grueso del tráfico de landings), no cargamos Firebase (~50 KB gz).
        // hasFirebaseSession lee la persistencia de Firebase (localStorage +
        // IndexedDB) sin importar el SDK, así que es correcto para usuarios
        // logueados de cualquier antigüedad.
        const { hasFirebaseSession } = await import('@/lib/has-firebase-session');
        if (!(await hasFirebaseSession())) {
          setAuthReady(true);
          return;
        }

        // Cargar Firebase de forma lazy
        const [{ auth }, { onAuthStateChanged }] = await Promise.all([
          import('@/lib/firebase-client'),
          import('firebase/auth')
        ]);
        
        // Guardar referencias para uso posterior (logout)
        authInstanceRef.current = auth;
        firebaseAuthRef.current = { onAuthStateChanged };
        
        if (!auth) {
          setAuthReady(true);
          return;
        }
        
        unsubscribe = onAuthStateChanged(auth, (u) => {
          setUser(u || null);
          setUserEmail(u?.email ?? null);
          setAuthReady(true);
        });
      } catch (err) {
        console.warn('Firebase auth init failed:', err);
        setAuthReady(true);
      }
    };

    // Diferir la carga de Firebase para no bloquear el LCP
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => initAuth(), { timeout: 3000 });
    } else {
      setTimeout(initAuth, 100);
    }

    return () => unsubscribe();
  }, []);

  // Productos (solo si hay usuario logueado)
  useEffect(() => {
    if (!authReady || !userEmail) return;
    
    let cancelled = false;
    
    const loadProducts = async () => {
      try {
        // Lazy load de apiWithAuth
        const { apiWithAuth } = await import('@/lib/auth-fetch');
        
        const res = await apiWithAuth({
          method: 'GET',
          url: 'api/portal/my-products',
          params: { email: userEmail },
        });
        
        if (cancelled) return;
        
        const list = Array.isArray(res?.data?.products)
          ? res.data.products.map((x) => Number(x)).filter((n) => Number.isFinite(n))
          : [];
        setUserProducts(list);
      } catch {
        setUserProducts([]);
      }
    };

    loadProducts();

    return () => { cancelled = true; };
  }, [authReady, userEmail]);

  // Estado de suscripción (para apps con accessMode: 'subscription', ej. BCM Nodes)
  useEffect(() => {
    if (!authReady || !userEmail) { setIsSubscriber(false); return; }

    let cancelled = false;

    const loadSubscription = async () => {
      try {
        const { apiWithAuth } = await import('@/lib/auth-fetch');

        const res = await apiWithAuth({
          method: 'GET',
          url: 'api/portal/my-subscription',
          params: { email: userEmail },
        });

        if (cancelled) return;
        setIsSubscriber(!!res?.data?.active);
      } catch {
        if (!cancelled) setIsSubscriber(false);
      }
    };

    loadSubscription();

    return () => { cancelled = true; };
  }, [authReady, userEmail]);

  // Usar i18n.language como fuente principal del locale (viene del servidor)
  // Fallback a pathname solo si i18n no está listo
  const SUPPORTED_LOCALES = VISIBLES;
  const i18nLocale = i18n?.language;
  const pathSegment = pathname?.split('/')[1] || '';
  const pathLocale = SUPPORTED_LOCALES.includes(pathSegment) ? pathSegment : null;
  const locale = (i18nLocale && SUPPORTED_LOCALES.includes(i18nLocale)) ? i18nLocale : pathLocale;

  // El query string del visitante (cc, utm, fbclid, email) se sigue arrastrando en los
  // links del menú, pero recién en el navegador y después de hidratar. En el render del
  // server NO: ese HTML se cachea (ISR y CloudFront, que no tiene el query en la key) y
  // el menú les salía a TODOS con el fbclid, los utm y hasta el ?email= del visitante
  // que había disparado el render. Medido el 9-oct-2026: 7 de 33 páginas de prod, y
  // unos 40 a 75 visitantes por día navegando con un fbclid ajeno. El _rsc se saca
  // siempre: es de los pedidos internos de Next y no es de nadie.
  const [qs, setQs] = useState('');
  const searchString = searchParams.toString();
  useEffect(() => {
    const propios = new URLSearchParams(searchString);
    propios.delete('_rsc');
    setQs(propios.toString());
  }, [searchString]);

  const buildHref = (path, opts = {}) => {
    const cleaned = path.startsWith('/') ? path : `/${path}`;

    const shouldSkipLocale =
      opts.noLocale ||
      !locale ||
      /^https?:\/\//i.test(cleaned) ||
      cleaned.startsWith('/app/');

    if (shouldSkipLocale) {
      if (opts.stripQuery) return cleaned;
      return qs ? `${cleaned}?${qs}` : cleaned;
    }
    // La home va sin barra final: /es/ contesta un 308 a /es (el logo lo pagaba en cada click).
    const base = cleaned === '/' ? `/${locale}` : `/${locale}${cleaned}`;
    return qs ? `${base}?${qs}` : base;
  };

  useEffect(() => {
    setTimeout(scrollToElement, 1000);
    init_classic_menu_resize();
    window.addEventListener('resize', init_classic_menu_resize);
    return () => window.removeEventListener('resize', init_classic_menu_resize);
  }, []);

  // 🔑 Logout con lazy load de signOut
  const handleLogout = useCallback(async () => {
    try {
      const auth = authInstanceRef.current;
      if (!auth) return;
      
      // Lazy load signOut
      const { signOut } = await import('firebase/auth');
      await signOut(auth);
      router.replace(buildHref('/'));
    } catch (e) {
      console.error('logout error', e);
    }
  }, [router, buildHref]);

  // Acceso
  const isLoggedIn = !!user;
  const canAccess = (appKey) => {
    // Apps gratis (ej. Grafos 3D por Matriz): ni cuenta, ni libros, ni suscripción.
    if (APP_UI[appKey].accessMode === 'free') return true;
    if (!isLoggedIn) return false;
    // Apps por login: alcanza con la sesión, sin libros ni suscripción.
    if (APP_UI[appKey].accessMode === 'login') return true;
    // Apps por suscripción (ej. BCM Nodes): requieren suscripción activa, no libros.
    if (APP_UI[appKey].accessMode === 'subscription') return isSubscriber;
    const setKey = APP_UI[appKey].accessSetKey;
    const allowSet = PRODUCT_ACCESS[setKey] || new Set();
    return userProducts.some((id) => allowSet.has(id));
  };
  const displayIcon = (appKey) => {
    const active = !isLoggedIn || canAccess(appKey);
    return active ? APP_UI[appKey].icons.active : APP_UI[appKey].icons.block;
  };

  /* === ToolLink === */
  const ToolLink = ({ appKey }) => {
    const href = buildHref(APP_UI[appKey].href, { noLocale: true, stripQuery: true });

    const onClick = (e) => {
      e.preventDefault();

      if (!canAccess(appKey)) {
        setModalAppKey(appKey);
        setModalOpen(true);
        return;
      }

      const abs = new URL(href, window.location.origin).toString();
      window.open(abs, '_blank', 'noopener,noreferrer');
      closeTools();
    };

    return (
      <a
        href={href}
        onClick={onClick}
        target="_blank"
        rel="noopener noreferrer"
        referrerPolicy="no-referrer"
        className="no-underline"
        style={{ display: 'flex', alignItems: 'center', padding: '10px 12px' }}
      >
        <Image
          src={displayIcon(appKey)}
          alt={t(APP_UI[appKey].nameKey)}
          width={24}
          height={24}
          style={{ marginRight: 12 }}
        />
        <span style={{ fontWeight: 500 }}>{t(APP_UI[appKey].nameKey)}</span>
      </a>
    );
  };

  const handleToolsActivator = (e) => {
    e.preventDefault();
    router.push(buildHref('/apps'));
  };

  return (
    <div className="main-nav-sub full-wrapper" style={{ position: "relative" }}>
      <div className="nav-logo-wrap local-scroll">
        {disableLogoLink ? (
          <span className="logo" style={{ cursor: 'default' }}>
            <div style={{ width: 200 }}>
              <BcmLogo />
            </div>
          </span>
        ) : (
          <a href={buildHref('/')} className="logo">
            <div style={{ width: 200 }}>
              <BcmLogo />
            </div>
          </a>
        )}
      </div>

      {/* Mobile Menu Button */}
      {!hideMenu && (
        <div onClick={toggleMobileMenu} className="mobile-nav" role="button" tabIndex={0}>
          <i className="mobile-nav-icon" />
          <span className="visually-hidden">{t('menu')}</span>
        </div>
      )}

      <div className="inner-nav desktop-nav" style={hideMenu ? { display: 'none' } : undefined}>
        {/* Izquierda */}
        <ul className="clearlist local-scroll">
          <li>
            <Link href={buildHref('/sobre-mi')}>{t('header_about_me')}</Link>
          </li>
          <li>
            <Link href={buildHref('/musica')}>{t('header_music')}</Link>
          </li>
          <li>
            <a href={buildHref('/libros')}>{t('header_books')}</a>
          </li>
          <li>
            <Link href={buildHref('/blog')}>{t('header_blog')}</Link>
          </li>

          {ENABLE_TOOLS_MENU && (
            <li
            >
              {/* href real y no "#": era el unico link del sitio hacia /apps y Google
                  no sigue un onClick, asi que toda la seccion colgaba solo del sitemap
                  (Search Console: 44 URLs de apps nunca rastreadas). El click sigue
                  navegando con el router. */}
              <a
                href={buildHref('/apps')}
                className="no-underline"
                onClick={handleToolsActivator}
                onTouchEnd={handleToolsActivator}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: 8 }}>
                  <g fill="#888888">
                    <circle cx="4" cy="4" r="2.2" />
                    <circle cx="12" cy="4" r="2.2" />
                    <circle cx="20" cy="4" r="2.2" />
                    <circle cx="4" cy="12" r="2.2" />
                    <circle cx="12" cy="12" r="2.2" />
                    <circle cx="20" cy="12" r="2.2" />
                    <circle cx="4" cy="20" r="2.2" />
                    <circle cx="12" cy="20" r="2.2" />
                    <circle cx="20" cy="20" r="2.2" />
                  </g>
                </svg>
                <span>{t('header_tools')}</span>
              </a>
            </li>
          )}
        </ul>

        {/* Derecha */}
        <ul className="items-end clearlist">
          {user && (
            <li>
              <Link href={buildHref('/portal')}>
                {t('header_portal') ?? 'Portal'}
              </Link>
            </li>
          )}

          <li>
            {user ? (
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleLogout(); }}
                className="no-underline cursor-pointer"
              >
                {t('header_logout') ?? 'Salir'}
              </a>
            ) : (
              <Link href={buildHref('/login')}>{t('header_login')}</Link>
            )}
          </li>

          <LanguageSelect />
        </ul>
      </div>

      <style jsx global>{`
        .inner-nav .tools-menu .mn-sub a,
        .inner-nav .tools-menu .mn-sub a:link,
        .inner-nav .tools-menu .mn-sub a:visited,
        .inner-nav .tools-menu .mn-sub a span {
          opacity: 1 !important;
          color: #444 !important;
        }
        .inner-nav .tools-menu .mn-sub a:hover {
          background: #f6f7f9;
          border-radius: 8px;
        }
        .inner-nav .tools-menu .mn-sub.closed { display: none !important; }
        .inner-nav .tools-menu .mn-sub.open { display: block !important; }
        .mobile-on .inner-nav .tools-menu .mn-sub.closed { display: none !important; }
        .mobile-on .inner-nav .tools-menu .mn-sub.open { display: block !important; }

        /* Logo centrado solo en mobile */
        @media (max-width: 1024px) {
          .main-nav-sub .nav-logo-wrap {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
          }
        }
      `}</style>

      {/* Modal de paywall por app */}
      <Modal open={modalOpen && !!modalAppKey} onClose={() => setModalOpen(false)}>
        {modalAppKey && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <h3 id="app-lock-title" style={{ margin: 0 }}>
                {t(APP_UI[modalAppKey].nameKey)}
              </h3>
            </div>

            <p style={{ marginTop: 12, marginBottom: 20, lineHeight: 1.5 }}>
              {t(APP_UI[modalAppKey].i18n.message)}
            </p>

            <div className="theme-elegant d-flex gap-2 justify-content-end mt-2">
              <a
                onClick={() => setModalOpen(false)}
                className="btn btn-mod btn-border btn-circle btn-small"
                data-btn-animate="y"
              >
                {t('common:close')}
              </a>

              <Link
                href={conOrigen(buildHref(APP_UI[modalAppKey].buyPath), `menu:${modalAppKey}`)}
                className="btn btn-mod btn-border btn-circle btn-small"
                data-btn-animate="y"
              >
                {t(APP_UI[modalAppKey].i18n.cta)}
              </Link>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
